import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { Leave } from 'src/app/classes/leave';
import { Project } from 'src/app/classes/project.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import {
    SelectProjectDialogComponent,
    SelectProjectDialogData,
    SelectProjectDialogResult,
} from 'src/app/components/select-project-dialog/select-project-dialog.component';
import {
    DuplicateEntrySelectDialogComponent,
    DuplicateEntrySelectDialogData,
    DuplicateEntrySelectDialogResult,
} from 'src/app/duplicate-entry-select-dialog/duplicate-entry-select-dialog.component';
import {
    ExecutorProjectChoiceDialogComponent,
    ExecutorProjectChoiceDialogData,
    ExecutorProjectChoiceDialogResult,
} from 'src/app/executor-project-choice-dialog/executor-project-choice-dialog.component';
import {
    SelectExecutorDialogComponent,
    SelectExecutorDialogData,
    SelectExecutorDialogResult,
} from 'src/app/select-executor-dialog/select-executor-dialog.component';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { PlanningDetailDialogService } from 'src/app/services/dialog/planning-detail-dialog.service';
import {
    AsphaltPerformerPlanningEntry,
    BulkUpdateExecutorPlanningRequest,
    CreateExecutorPlanningRequest,
    ExecutorPlanningService,
    ProjectExecutorPlanningEntry,
    UpdateExecutorPlanningRequest,
} from 'src/app/services/executor-planning.service';
import { LeaveService } from 'src/app/services/leave.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserService } from 'src/app/services/user/user.service';
import {
    ShiftDateRangeDialogComponent,
    ShiftDateRangeDialogData,
    ShiftDateRangeDialogResult,
} from 'src/app/shift-date-range-dialog/shift-date-range-dialog.component';
import { ExecutorPlanningSettings } from 'src/app/storage.class';
import { DAY_IN_MILLISECONDS, Utils } from 'src/app/utils.class';
import { environment } from 'src/environments/environment';
import { ProjectEditDialogComponent } from '../projects/project-edit-dialog/project-edit-dialog.component';
import {
    ACTIVITIES,
    ActivityOption,
    ChainEntry,
    ContextMenuData,
    DuplicateEntry,
    ExecutorSchedule,
    PlanningCell,
    ProjectSchedule,
    TIME_OPTIONS,
    TimeOption,
    VisibleWeeks,
} from './activity-constants';

@Component({
    selector: 'app-project-executor-planning',
    standalone: false,
    templateUrl: './project-executor-planning.component.html',
    styleUrls: ['./project-executor-planning.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectExecutorPlanningComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('planningContextMenuTrigger') contextMenuTrigger!: MatMenuTrigger;
    private readonly DEFAULT_WEEKS_TO_DISPLAY = 15;
    private readonly FULL_YEAR_WEEKS = 52;
    private readonly PREVIOUS_WEEKS_TO_DISPLAY = 5;
    private readonly asphaltEntityTypeActivityMap: Record<string, string[]> = {
        asfaltTeam: ['asfalt', 'frezen_asfalteren'],
        cutter: ['frezen', 'frezen_asfalteren'],
    };
    environment = environment;
    readonly = false;
    showHelp = false;

    // Data properties
    planningData: ProjectExecutorPlanningEntry[] = [];
    users: User[] = [];
    projects: Project[] = [];
    executorScheduleArray: ExecutorSchedule[] = [];

    // Cache for manually added projects (executor_id -> {projectId: Project}[])
    private addedProjectsCache: Map<number, Map<string, Project>> = new Map();

    // Track if projects have been sorted (only sort on first load)
    private projectsHaveBeenSorted = false;

    private planningDataSubject: BehaviorSubject<ProjectExecutorPlanningEntry[]> = new BehaviorSubject<
        ProjectExecutorPlanningEntry[]
    >(null);
    planningData$ = this.planningDataSubject.asObservable().pipe(skipWhile(v => !v));

    private executorScheduleSubject: BehaviorSubject<ExecutorSchedule[]> = new BehaviorSubject<ExecutorSchedule[]>(
        null
    );
    executorSchedule$ = this.executorScheduleSubject.asObservable();

    private usersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
    users$ = this.usersSubject.asObservable().pipe(skipWhile(v => !v));

    private asphaltEntriesSubject: BehaviorSubject<AsphaltPerformerPlanningEntry[]> = new BehaviorSubject<
        AsphaltPerformerPlanningEntry[]
    >([]);
    asphaltEntries$ = this.asphaltEntriesSubject.asObservable();

    visibleDates: string[] = [];
    visibleWeeks: VisibleWeeks[] = []; // Cache this to prevent recalculation

    isLoading = signal<boolean>(true);

    contextMenuData: ContextMenuData = {
        executor: null,
        project: null,
        date: '',
        hasEntry: false,
        isChain: false,
        isNightMode: false,
    };

    activities: ActivityOption[] = ACTIVITIES;

    timeOptions: TimeOption[] = TIME_OPTIONS;

    hideUnplannedExecutorsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    hideUnplannedExecutors$ = this.hideUnplannedExecutorsSubject.asObservable();

    showFullYearSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    showFullYear$ = this.showFullYearSubject.asObservable();

    showPreviousWeeksSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    showPreviousWeeks$ = this.showPreviousWeeksSubject.asObservable();

    showProjectNumberSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    showProjectNumber$ = this.showProjectNumberSubject.asObservable();

    selectedProjectManagerSubject: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
    selectedProjectManager$ = this.selectedProjectManagerSubject.asObservable();

    selectedProjectManagerId: number | null = null;

    projectManagers: User[] = [];
    leaves: Leave[] = [];

    // Selected view options for the dropdown
    selectedViewOptions: string[] = this.loadViewOptions();

    // Property to track expand/collapse all state
    allExecutorsExpandedState: { checked: boolean; indeterminate: boolean } = { checked: false, indeterminate: false };

    constructor(
        private readonly dialog: MatDialog,
        private readonly executorPlanningService: ExecutorPlanningService,
        private readonly userService: UserService,
        private readonly confirmDialogService: ConfirmDialogService,
        private readonly leaveService: LeaveService,
        private readonly title: Title,
        private readonly planningService: PlanningService,
        private readonly planningDetailDialogService: PlanningDetailDialogService
    ) {
        super();
        const savedOptions = this.loadViewOptions();
        this.visibleDates = this.getVisibleDates(
            this.DEFAULT_WEEKS_TO_DISPLAY,
            savedOptions.includes('showPreviousWeeks')
        );
        this.visibleWeeks = this.getVisibleWeeks();
        this.hideUnplannedExecutorsSubject.next(savedOptions.includes('hideUnplanned'));
        this.showFullYearSubject.next(savedOptions.includes('showFullYear'));
        this.showPreviousWeeksSubject.next(savedOptions.includes('showPreviousWeeks'));
        this.showProjectNumberSubject.next(savedOptions.includes('showProjectNumber'));
        this.readonly = !AuthorisationService.hasFunctionality(this.Functionalities.ExecutorPlanning);
    }

    ngOnInit(): void {
        this.loadInitialData();

        this.title.setTitle('Uitvoerdersplanning ' + environment.titleAppend);

        // Load project managers
        this.subscriptions.add(
            this.userService.getByType(this.environment.roles.Projectmanager).subscribe({
                next: users => {
                    this.projectManagers = users;
                },
                error: error => {
                    console.error('Error loading project managers:', error);
                },
            })
        );

        // Subscribe to show full year changes
        this.subscriptions.add(
            this.showFullYear$.subscribe(showFullYear => {
                const weeksToShow = showFullYear ? this.FULL_YEAR_WEEKS : this.DEFAULT_WEEKS_TO_DISPLAY;
                this.visibleDates = this.getVisibleDates(weeksToShow, this.showPreviousWeeksSubject.getValue());
                this.visibleWeeks = this.getVisibleWeeks();

                // Reload data with new date range
                this.loadInitialData();
            })
        );

        // Subscribe to show previous weeks changes
        this.subscriptions.add(
            this.showPreviousWeeks$.subscribe(showPreviousWeeks => {
                const weeksToShow = this.showFullYearSubject.getValue()
                    ? this.FULL_YEAR_WEEKS
                    : this.DEFAULT_WEEKS_TO_DISPLAY;
                this.visibleDates = this.getVisibleDates(weeksToShow, showPreviousWeeks);
                this.visibleWeeks = this.getVisibleWeeks();

                // Reload data with new date range
                this.loadInitialData();
            })
        );

        this.subscriptions.add(
            combineLatest([
                this.users$,
                this.planningData$,
                this.hideUnplannedExecutors$,
                this.showProjectNumber$,
                this.selectedProjectManager$,
                this.asphaltEntries$,
            ])
                .pipe(
                    map(
                        ([
                            users,
                            planningData,
                            hideUnplannedExecutors,
                            showProjectNumber,
                            selectedProjectManagerId,
                            asphaltEntries,
                        ]) => {
                            const executorScheduleArray = users.map(executor => {
                                const executorPlanningEntries = (planningData as ProjectExecutorPlanningEntry[]).filter(
                                    entry => entry.user_id === executor.id
                                );

                                const projectsMap = new Map<string, ProjectSchedule>();

                                executorPlanningEntries.forEach(entry => {
                                    const afasProjectId = entry.afas_project_id;

                                    if (!projectsMap.has(afasProjectId)) {
                                        const project = entry.project;
                                        const isExecutorAssigned = project;

                                        if (isExecutorAssigned) {
                                            projectsMap.set(afasProjectId, {
                                                project,
                                                afasProjectId,
                                                planningEntries: [],
                                                planningByDate: {},
                                                cells: [],
                                            });

                                            // Remove from cache since it now has actual planning data
                                            this.removeCachedProject(executor.id, afasProjectId);
                                        }
                                    }

                                    const projectSchedule = projectsMap.get(afasProjectId);
                                    if (projectSchedule) {
                                        projectSchedule.planningEntries.push(entry);
                                        const dateKey = entry.date.substring(0, 10);
                                        projectSchedule.planningByDate[dateKey] = entry;
                                    }
                                });

                                // Add cached projects that don't have planning entries yet
                                const cachedProjects = this.addedProjectsCache.get(executor.id);
                                if (cachedProjects) {
                                    cachedProjects.forEach((project, projectId) => {
                                        if (!projectsMap.has(projectId)) {
                                            projectsMap.set(projectId, {
                                                project: project,
                                                afasProjectId: projectId,
                                                planningEntries: [],
                                                planningByDate: {},
                                                cells: [],
                                            });
                                        }
                                    });
                                }

                                const executorAsphaltEntries = (
                                    asphaltEntries as AsphaltPerformerPlanningEntry[]
                                ).filter(e => Number(e.user_id) === executor.id);
                                const asphaltProjectsMap = new Map<string, Set<string>>();
                                executorAsphaltEntries.forEach(e => {
                                    if (!asphaltProjectsMap.has(e.afas_project_id)) {
                                        asphaltProjectsMap.set(e.afas_project_id, new Set<string>());
                                    }
                                    const dateKey = (e.date || '').substring(0, 10);
                                    if (dateKey) {
                                        asphaltProjectsMap.get(e.afas_project_id).add(dateKey);
                                    }
                                });

                                const projectsForExecutor = Array.from(projectsMap.values()).map(projectSchedule => {
                                    const asphaltDates =
                                        asphaltProjectsMap.get(projectSchedule.afasProjectId) || new Set<string>();
                                    const cells = this.visibleDates.map(date => {
                                        const entry = projectSchedule.planningByDate[date];
                                        const hasAsphalt = asphaltDates.has(date);

                                        // Find chain of IDs for this cell
                                        const chainEntries = entry ? this.findCellChain(projectSchedule, date) : [];
                                        if (entry) {
                                            entry.chain = chainEntries;
                                        }

                                        const formattedDate = Utils.formatDate(date, 'EEE d MMM yyyy', 'nl');
                                        let title = formattedDate;

                                        // Add activity to title if entry exists
                                        if (entry) {
                                            const activityOption = ACTIVITIES.find(a => a.value === entry.activity);
                                            const activityLabel = activityOption
                                                ? activityOption.label
                                                : entry.activity;
                                            title = `${activityLabel}\n${formattedDate}`;
                                        }

                                        if (hasAsphalt) {
                                            title = `Asfalt\n${title}`;
                                        }

                                        // Add project number to title if enabled
                                        if (showProjectNumber && projectSchedule.project?.afas_project_id) {
                                            title = `${projectSchedule.project.afas_project_id}\n${title}`;
                                        }

                                        // Get unique planning entries for this date
                                        const uniquePlanningEntries = this.getUniqueEntriesForDate(
                                            projectSchedule.planningEntries,
                                            date
                                        );

                                        // Get asphalt entries for this date and project
                                        const asphaltEntriesForDate = executorAsphaltEntries.filter(
                                            e =>
                                                e.afas_project_id === projectSchedule.afasProjectId &&
                                                e.date.substring(0, 10) === date
                                        );

                                        const executorActivities = new Set(
                                            uniquePlanningEntries.map(planningEntry => planningEntry.activity)
                                        );

                                        // Suppress duplicate only when asphalt planning entity type maps
                                        // to one of the executor activities for that same day/project.
                                        const conflictingAsphaltEntries = asphaltEntriesForDate.filter(asphaltEntry => {
                                            return !this.hasMatchingExecutorActivity(asphaltEntry, executorActivities);
                                        });

                                        const hasFrezenAsphalt = conflictingAsphaltEntries.some(e =>
                                            this.asphaltEntityTypeActivityMap[e.entitytype_key]?.includes('frezen')
                                        );
                                        const hasAsfaltAsphalt = conflictingAsphaltEntries.some(e =>
                                            this.asphaltEntityTypeActivityMap[e.entitytype_key]?.includes('asfalt')
                                        );
                                        const isFrezenAsfalt = hasFrezenAsphalt && hasAsfaltAsphalt;

                                        const duplicateEntries: DuplicateEntry[] = [...uniquePlanningEntries];
                                        const hasDuplicate = uniquePlanningEntries.length > 1;

                                        const asphaltPlanningId = asphaltEntriesForDate[0]?.planning_id ?? null;

                                        const hasCutterEntry = asphaltEntriesForDate.some(
                                            e => e.entitytype_key === 'cutter'
                                        );
                                        const hasAsfaltTeamEntry = asphaltEntriesForDate.some(
                                            e => e.entitytype_key === 'asfaltTeam'
                                        );
                                        let asphaltIndicatorLetter = 'A';
                                        if (hasCutterEntry && hasAsfaltTeamEntry) {
                                            asphaltIndicatorLetter = 'FA';
                                        } else if (hasCutterEntry) {
                                            asphaltIndicatorLetter = 'F';
                                        }

                                        return {
                                            date,
                                            entry,
                                            hasEntry: !!entry,
                                            hasDuplicate,
                                            duplicateEntries,
                                            isWeekend: this.isWeekend(date),
                                            cssClasses: [
                                                this.isWeekend(date) ? 'weekend-cell' : '',
                                                entry ? this.getTimeColorClass(entry) : '',
                                            ].filter(cls => cls !== ''),
                                            hasAsphalt,
                                            hasLeave: this.hasLeaveOnDate(executor.id, date),
                                            chain: chainEntries,
                                            title: title,
                                            isFrezenAsfalt,
                                            asphaltPlanningId,
                                            asphaltIndicatorLetter,
                                        };
                                    });

                                    return {
                                        ...projectSchedule,
                                        cells,
                                    };
                                });

                                // Sort projects by earliest planning date (ascending), then by project number
                                // Only sort on first load to maintain user's manual order afterwards
                                let sortedProjectsForExecutor = projectsForExecutor;
                                if (!this.projectsHaveBeenSorted) {
                                    sortedProjectsForExecutor = projectsForExecutor.sort((a, b) => {
                                        // Find earliest planning date for project A
                                        const aEarliestDate = this.getEarliestPlanningDate(a.planningByDate);

                                        // Find earliest planning date for project B
                                        const bEarliestDate = this.getEarliestPlanningDate(b.planningByDate);

                                        // If both have planning dates, sort by earliest date (ascending)
                                        if (aEarliestDate && bEarliestDate) {
                                            return aEarliestDate.localeCompare(bEarliestDate);
                                        }

                                        // Projects with planning come before projects without planning
                                        if (aEarliestDate && !bEarliestDate) return -1;
                                        if (!aEarliestDate && bEarliestDate) return 1;

                                        // If neither has planning, sort by project number
                                        const aProjectNumber = a.project?.afas_project_id || '';
                                        const bProjectNumber = b.project?.afas_project_id || '';
                                        return aProjectNumber.localeCompare(bProjectNumber, undefined, {
                                            numeric: true,
                                        });
                                    });
                                }

                                return {
                                    executor,
                                    projects: sortedProjectsForExecutor,
                                };
                            });

                            // Filter by selected project manager
                            let filteredExecutorScheduleArray = executorScheduleArray.map(executorSchedule => {
                                if (selectedProjectManagerId === null) {
                                    return executorSchedule;
                                }

                                // Filter projects by project manager
                                const filteredProjects = executorSchedule.projects.filter(
                                    projectSchedule =>
                                        projectSchedule.project.projectmanager_id === selectedProjectManagerId
                                );

                                return {
                                    ...executorSchedule,
                                    projects: filteredProjects,
                                };
                            });

                            // Filter out executors without planning if hideUnplannedExecutors is enabled
                            if (hideUnplannedExecutors) {
                                filteredExecutorScheduleArray = filteredExecutorScheduleArray.filter(
                                    executorSchedule => {
                                        return executorSchedule.projects.some(
                                            project => project.planningEntries.length > 0
                                        );
                                    }
                                );
                            }

                            // Sort executors: first those with planning, then those without, both groups alphabetically
                            const sortedExecutorScheduleArray = filteredExecutorScheduleArray.sort((a, b) => {
                                const aHasPlanning = a.projects.some(project => project.planningEntries.length > 0);
                                const bHasPlanning = b.projects.some(project => project.planningEntries.length > 0);

                                // If one has planning and the other doesn't, prioritize the one with planning
                                if (aHasPlanning && !bHasPlanning) {
                                    return -1;
                                }
                                if (!aHasPlanning && bHasPlanning) {
                                    return 1;
                                }

                                // Both have same planning status, sort alphabetically by name
                                const aName = `${a.executor.name}`.toLowerCase();
                                const bName = `${b.executor.name}`.toLowerCase();

                                return aName.localeCompare(bName, 'nl');
                            });

                            return sortedExecutorScheduleArray;
                        }
                    )
                )
                .subscribe({
                    next: (executorScheduleArray: ExecutorSchedule[]) => {
                        this.executorScheduleSubject.next(executorScheduleArray);
                        this.executorScheduleArray = executorScheduleArray;
                        this.updateExpandAllState();
                        // Mark that projects have been sorted on first load
                        if (!this.projectsHaveBeenSorted) {
                            this.projectsHaveBeenSorted = true;
                        }
                    },
                })
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy?.();
    }

    private loadInitialData(): void {
        this.isLoading.set(true);
        const { startDate, endDate } = this.getDateRange();

        this.subscriptions.add(
            this.userService.getByType(this.environment.roles.Executor).subscribe({
                next: users => {
                    this.usersSubject.next(users);
                    this.users = users;
                },
                error: error => {
                    this.isLoading.set(false);
                },
            })
        );

        const endDatePlusOne = endDate.setDate(endDate.getDate() + 1);

        this.subscriptions.add(
            this.executorPlanningService.getFilteredList(startDate, new Date(endDatePlusOne)).subscribe({
                next: executorSchedule => {
                    this.planningDataSubject.next(executorSchedule);

                    // Extract unique projects from planning data
                    const projectsMap = new Map<string, Project>();
                    executorSchedule.forEach(entry => {
                        if (entry.project && entry.project.afas_project_id) {
                            projectsMap.set(entry.project.afas_project_id, entry.project);
                        }
                    });
                    this.projects = Array.from(projectsMap.values());

                    this.isLoading.set(false);
                },
                error: error => {
                    this.isLoading.set(false);
                },
            })
        );

        // Load leave data
        const endDateObj = new Date(endDatePlusOne);
        this.subscriptions.add(
            this.leaveService.getExecutorLeaves(startDate.toISOString(), endDateObj.toISOString()).subscribe({
                next: response => {
                    // Filter leaves to only include those in the visible date range
                    this.leaves = response.data.filter(leave =>
                        this.leaveService.itemInDaterange(leave, startDate, endDateObj)
                    );
                },
                error: error => {
                    console.error('Error loading leaves:', error);
                },
            })
        );

        // Load asphalt performer planning
        this.subscriptions.add(
            this.executorPlanningService
                .getAsphaltPerformerPlanning(startDate, endDateObj)
                .pipe(
                    map(entries => entries.filter(entry => !!this.asphaltEntityTypeActivityMap[entry.entitytype_key]))
                )
                .subscribe({
                    next: entries => {
                        this.asphaltEntriesSubject.next(entries);
                    },
                    error: () => {
                        /* non-critical, silently ignore */
                    },
                })
        );
    }

    /**
     * Check if executor has leave on a specific date
     */
    hasLeaveOnDate(executorId: number, date: string): boolean {
        const dateObj = new Date(date);
        return this.leaves.some(leave => {
            // Check if leave applies to this executor (either common or user-specific)
            const appliesToExecutor = leave.common || (leave.users && leave.users.some(u => u.id === executorId));

            if (!appliesToExecutor) {
                return false;
            }

            // Check if date is within leave period
            const leaveStart = new Date(leave.begindate);
            const leaveEnd = new Date(leave.enddate);
            const isInRange = dateObj >= leaveStart && dateObj <= leaveEnd;

            if (!isInRange) {
                return false;
            }

            // Check if this specific date is excluded
            if (leave.excluded && leave.excluded.length > 0) {
                const isExcluded = leave.excluded.some(exc => {
                    const excDate = new Date(exc.date);
                    return (
                        excDate.toDateString() === dateObj.toDateString() &&
                        (!exc.user_id || exc.user_id === executorId)
                    );
                });
                if (isExcluded) {
                    return false;
                }
            }

            // Check for interval_week pattern
            if (leave.interval_week > 0) {
                const firstWeek = Utils.getWeekNumber(new Date(leave.begindate).toISOString().substring(0, 10));
                const currentWeek = Utils.getWeekNumber(date);
                const weekMatch = (currentWeek - firstWeek) % leave.interval_week === 0;
                if (!weekMatch) {
                    return false;
                }
            }

            // Check day of week patterns
            const dayOfWeek = dateObj.getDay();
            const dayPatterns = {
                0: leave.repeat_sunday,
                1: leave.repeat_monday,
                2: leave.repeat_tuesday,
                3: leave.repeat_wednesday,
                4: leave.repeat_thursday,
                5: leave.repeat_friday,
                6: leave.repeat_saturday,
            };

            // If any day pattern is set, check if this day matches
            const hasAnyDayPattern =
                leave.repeat_monday ||
                leave.repeat_tuesday ||
                leave.repeat_wednesday ||
                leave.repeat_thursday ||
                leave.repeat_friday ||
                leave.repeat_saturday ||
                leave.repeat_sunday;

            if (hasAnyDayPattern) {
                return dayPatterns[dayOfWeek];
            }

            return true;
        });
    }

    /**
     * Find chain of connected cells with same project, activity, and time (day/night)
     * Returns array of entry IDs and dates that are part of the chain
     */
    private findCellChain(projectSchedule: ProjectSchedule, targetDate: string): ChainEntry[] {
        const targetEntry = projectSchedule.planningByDate[targetDate];
        if (!targetEntry) return [];

        const targetTimeType = this.getTimeType(targetEntry);
        const targetActivity = targetEntry.activity;
        const sortedDates = this.visibleDates.slice().sort();
        const targetIndex = sortedDates.indexOf(targetDate);

        if (targetIndex === -1) return [];

        const chainEntries: ChainEntry[] = [];
        const processedDates = new Set<string>();

        // Start from target date and expand both directions
        const datesToCheck = [targetDate];
        processedDates.add(targetDate);
        chainEntries.push({ id: targetEntry.id, date: targetDate });

        while (datesToCheck.length > 0) {
            const currentDate = datesToCheck.shift()!;
            const currentIndex = sortedDates.indexOf(currentDate);

            // Check previous day
            if (currentIndex > 0) {
                const prevDate = sortedDates[currentIndex - 1];
                if (!processedDates.has(prevDate)) {
                    const prevEntry = projectSchedule.planningByDate[prevDate];
                    if (
                        prevEntry &&
                        prevEntry.activity === targetActivity &&
                        this.getTimeType(prevEntry) === targetTimeType
                    ) {
                        chainEntries.push({ id: prevEntry.id, date: prevDate });
                        datesToCheck.push(prevDate);
                        processedDates.add(prevDate);
                    }
                }
            }

            // Check next day
            if (currentIndex < sortedDates.length - 1) {
                const nextDate = sortedDates[currentIndex + 1];
                if (!processedDates.has(nextDate)) {
                    const nextEntry = projectSchedule.planningByDate[nextDate];
                    if (
                        nextEntry &&
                        nextEntry.activity === targetActivity &&
                        this.getTimeType(nextEntry) === targetTimeType
                    ) {
                        chainEntries.push({ id: nextEntry.id, date: nextDate });
                        datesToCheck.push(nextDate);
                        processedDates.add(nextDate);
                    }
                }
            }
        }

        return chainEntries.sort((a, b) => a.date.localeCompare(b.date));
    }

    /**
     * Get time type (day/night) for a planning entry
     */
    private getTimeType(entry: ProjectExecutorPlanningEntry): 'day' | 'night' {
        const hour = new Date(entry.date).getHours();
        return hour >= 8 && hour < 18 ? 'day' : 'night';
    }

    private hasChain(project, executor, date): boolean {
        const entry = this.getPlanningEntry(executor.id, project.afasProjectId, date);
        if (!entry) return false;

        const chain = this.findCellChain(
            this.getProjectScheduleForExecutor(executor.id, project.afasProjectId),
            entry.date
        );
        return chain.length > 1;
    }

    private getDateRange(): { startDate: Date; endDate: Date } {
        return {
            startDate: new Date(this.visibleDates[0]),
            endDate: new Date(this.visibleDates[this.visibleDates.length - 1]),
        };
    }

    getPlanningEntry(executorId: number, afasProjectId: string, date: string): ProjectExecutorPlanningEntry | null {
        const projectSchedule = this.getProjectScheduleForExecutor(executorId, afasProjectId);
        return projectSchedule ? this.getPlanningEntryFromProject(projectSchedule, date) : null;
    }

    getPlanningEntryFromProject(projectSchedule: ProjectSchedule, date: string): ProjectExecutorPlanningEntry | null {
        return projectSchedule.planningEntries.find(entry => entry.date.startsWith(date)) || null;
    }

    private getUniqueEntriesForDate(
        entries: ProjectExecutorPlanningEntry[],
        date: string
    ): ProjectExecutorPlanningEntry[] {
        const entriesForDate = entries.filter(entry => entry.date.startsWith(date));
        const entriesById = new Map<number, ProjectExecutorPlanningEntry>();

        entriesForDate.forEach(entry => {
            entriesById.set(entry.id, entry);
        });

        return Array.from(entriesById.values());
    }

    getExecutorScheduleById(executorId: number): ExecutorSchedule | undefined {
        return this.executorScheduleSubject.getValue().find(schedule => schedule.executor.id === executorId);
    }

    getProjectScheduleForExecutor(executorId: number, afasProjectId: string): ProjectSchedule | undefined {
        const executorSchedule = this.getExecutorScheduleById(executorId);
        return executorSchedule?.projects.find(project => project.afasProjectId === afasProjectId);
    }

    getCellByAttributes(executorId: number, projectId: string, date: string): PlanningCell | null {
        const projectSchedule = this.getProjectScheduleForExecutor(executorId, projectId);
        if (!projectSchedule) return null;
        return projectSchedule.cells.find(cell => cell.date === date) || null;
    }

    getExecutorById(executorId: number): User | null {
        return this.users.find(u => u.id === executorId) || null;
    }

    getProjectById(projectId: string): Project | null {
        return this.projects.find(p => p.afas_project_id === projectId) || null;
    }

    getVisibleDates(weeks = 15, includePreviousWeeks = false): string[] {
        const today = new Date();
        const diffToMonday = today.getDay() === 0 ? -6 : 1 - today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() + diffToMonday);

        if (includePreviousWeeks) {
            monday.setDate(monday.getDate() - this.PREVIOUS_WEEKS_TO_DISPLAY * 7);
            weeks += this.PREVIOUS_WEEKS_TO_DISPLAY;
        }

        return Array.from({ length: weeks * 7 }, (_, i) => {
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            return date.toISOString().slice(0, 10);
        });
    }

    getVisibleWeeks(): { number: number; dates: string[] }[] {
        const weeks: { number: number; dates: string[] }[] = [];
        let currentWeek: string[] = [];
        let currentWeekNr = Utils.getWeekNumber(this.visibleDates[0]);

        for (const date of this.visibleDates) {
            const weekNr = Utils.getWeekNumber(date);

            if (weekNr !== currentWeekNr && currentWeek.length) {
                weeks.push({ number: currentWeekNr, dates: currentWeek });
                currentWeek = [];
                currentWeekNr = weekNr;
            }

            currentWeek.push(date);
        }

        if (currentWeek.length) {
            weeks.push({ number: currentWeekNr, dates: currentWeek });
        }

        return weeks;
    }

    getTimeColorClass(entry: ProjectExecutorPlanningEntry): string {
        if (!entry?.date) return '';
        if (entry.activity === 'vrij') return 'vrij-cell';
        if (entry.activity === 'slaapdag') return 'slaapdag-cell';

        const hour = new Date(entry.date).getHours();

        if (hour >= 8 && hour < 18) return 'day-cell';
        return 'night-cell';
    }

    isWeekend(dateString: string): boolean {
        const day = new Date(dateString).getDay();
        return day === 0 || day === 6;
    }

    hasActivityForWeek(week: VisibleWeeks, executorScheduleArray: ExecutorSchedule[]): boolean {
        if (!executorScheduleArray || executorScheduleArray.length === 0) {
            return true; // No data means no red highlighting
        }

        const executorSchedule = executorScheduleArray[0]; // Since we're passing a single executor

        // If executor has no projects, don't highlight as red
        if (!executorSchedule.projects || executorSchedule.projects.length === 0) {
            return true;
        }

        // Check if any project has activities for this week
        return executorSchedule.projects.some(project =>
            project.cells.some(cell => week.dates.includes(cell.date) && cell.hasEntry)
        );
    }

    hasActivityForDay(date: string, executorSchedule: ExecutorSchedule): boolean {
        if (!executorSchedule?.projects || executorSchedule.projects.length === 0) {
            return true; // No projects means no red highlighting
        }

        return executorSchedule.projects.some(project =>
            project.cells.some(cell => cell.date === date && cell.hasEntry)
        );
    }

    /**
     * Remove a project from cache when it gets actual planning entries
     */
    private removeCachedProject(executorId: number, projectId: string): void {
        const cachedProjects = this.addedProjectsCache.get(executorId);
        if (cachedProjects) {
            cachedProjects.delete(projectId);
            if (cachedProjects.size === 0) {
                this.addedProjectsCache.delete(executorId);
            }
        }
    }

    onCellClick(event: MouseEvent): void {
        const resolved = this.resolveCellContextFromEvent(event);
        if (!resolved) {
            return;
        }

        let { executor, project, date, cell } = resolved;

        executor = resolved.executor;
        project = resolved.project;
        date = resolved.date;
        cell = resolved.cell;
        if ((cell?.hasAsphalt && !cell?.isFrezenAsfalt) || cell?.hasDuplicate || cell?.isFrezenAsfalt) {
            return;
        }

        if (AuthorisationService.hasFunctionality(this.Functionalities.ExecutorPlanning)) {
            if (event.ctrlKey) {
                event.preventDefault();
                this.onCtrlClick(executor, project, date);
                return;
            }

            if (event.shiftKey) {
                event.preventDefault();
                this.onShiftClick(executor, project, date);
                return;
            }

            this.onNormalClick(executor, project, date);
        }
    }

    onCellDblClick(event: MouseEvent): void {
        const resolved = this.resolveCellContextFromEvent(event);
        if (!resolved) {
            return;
        }

        const { cell } = resolved;

        if (!cell.asphaltPlanningId) {
            return;
        }

        this.subscriptions.add(
            this.planningService.getSingle(cell.asphaltPlanningId).subscribe({
                next: planning => {
                    this.planningDetailDialogService.openPlanning(planning);
                },
            })
        );
    }

    onExecutorNameClick(executor: User): void {
        if (!AuthorisationService.hasFunctionality(this.Functionalities.ExecutorPlanning)) {
            return;
        }
        const executorSchedule = this.getExecutorScheduleById(executor.id);
        const currentProjectIds = executorSchedule?.projects.map(p => p.afasProjectId) || [];

        const dialogData: SelectProjectDialogData = {
            executor: executor,
            currentProjectIds: currentProjectIds,
        };

        this.dialog
            .open(SelectProjectDialogComponent, {
                width: '400px',
                data: dialogData,
                disableClose: false,
            })
            .afterClosed()
            .subscribe((result: SelectProjectDialogResult) => {
                if (result) {
                    this.handleAddProject(result);
                }
            });
    }

    onProjectDoubleClick(project: Project): void {
        const dialogRef = this.dialog.open(ProjectEditDialogComponent, {
            width: '600px',
            data: { projectId: project.afas_project_id },
        });

        this.subscriptions.add(dialogRef.afterClosed().subscribe(result => {}));
    }

    private onNormalClick(executor: User, project: Project, date: string): void {
        const targetEntry = this.getPlanningEntry(executor.id, project.afas_project_id, date);

        if (!targetEntry) {
            // Get cell before this one
            const prevDate = new Date(date);
            prevDate.setDate(prevDate.getDate() - 1);
            const prevEntry = this.getPlanningEntry(
                executor.id,
                project.afas_project_id,
                prevDate.toISOString().slice(0, 10)
            );
            let activity = ACTIVITIES[0].value;
            if (prevEntry) {
                activity = prevEntry.activity;
            }

            const dateWithTime = new Date(date);
            if (this.contextMenuData.isNightMode || prevEntry?.date.includes('18:00')) {
                dateWithTime.setHours(20, 0, 0, 0); // Set to night time (20:00)
            } else {
                dateWithTime.setHours(8, 0, 0, 0); // Set to day time (08:00)
            }

            const newEntry = {
                executorId: executor.id,
                projectId: project.afas_project_id,
                date: dateWithTime.toISOString(),
                activity: activity,
            };

            this.subscriptions.add(
                this.executorPlanningService.createPlanning(newEntry).subscribe({
                    next: response => {
                        this.loadInitialData();
                    },
                })
            );

            return;
        }

        // get index of current activity
        const index = ACTIVITIES.findIndex(activity => activity.value === targetEntry.activity);

        const updatedEntry = {
            id: targetEntry.id,
            executorId: targetEntry.user_id,
            projectId: targetEntry.afas_project_id,
            date: targetEntry.date,
            activity: ACTIVITIES[(index + 1) % ACTIVITIES.length].value,
        };

        this.subscriptions.add(this.executorPlanningService.updatePlanning(updatedEntry).subscribe());
    }

    private onShiftClick(executor: User, project: Project, date: string): void {
        const sourceEntry = this.findFirstPlanningEntry(executor.id, project.afas_project_id, date);

        if (!sourceEntry) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen planning gevonden',
                    text: `Geen bestaande planning gevonden voor ${executor.name} op project ${project.name}. Kan geen planning kopiëren.`,
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {}); // Ignore rejection when dialog is closed
            return;
        }

        // Get all dates from the source entry date until the selected date
        const dateRange = this.getDateRangeFromTo(sourceEntry.date.substring(0, 10), date);

        if (dateRange.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Ongeldige datumreeks',
                    text: 'Geen geldige datumreeks gevonden. Zorg ervoor dat de geselecteerde datum na de eerste planning ligt.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        if (dateRange.length > 50) {
            this.confirmDialogService
                .confirm({
                    title: 'Te veel dagen',
                    text: 'Te veel dagen geselecteerd (max 50). Kies een kortere periode.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        // Check if dateRange includes weekends if so remove from the available dates
        const filteredDates = dateRange.filter(date => !this.isWeekend(date));
        if (filteredDates.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen geldige datums',
                    text: 'Alle geselecteerde datums zijn in het weekend.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        this.addBulkPlanningEntries(executor, project, sourceEntry, filteredDates);
    }

    /**
     * Find the first existing planning entry for the given executor, project, and date.
     */
    private findFirstPlanningEntry(
        executorId: number,
        afasProjectId: string,
        date
    ): ProjectExecutorPlanningEntry | null {
        const planningData = this.planningDataSubject.getValue();
        if (!planningData) return null;

        const executorEntries = planningData
            .filter(entry => entry.user_id === executorId && entry.afas_project_id === afasProjectId)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const target = new Date(date);

        const pastRecords = executorEntries.filter(obj => new Date(obj.date) <= target);

        if (pastRecords.length === 0) return null;

        return pastRecords.reduce((closest, record) => {
            return new Date(record.date) > new Date(closest.date) ? record : closest;
        });
    }

    private onCtrlClick(executor: User, project: Project, date: string): void {
        const targetEntry = this.getPlanningEntry(executor.id, project.afas_project_id, date);

        if (!targetEntry) {
            this.copyPreviousWeekPlanning(executor, project, date);
        } else {
            // Er is planning - dag naar nacht zetten (of andersom)
            const currentDate = new Date(targetEntry.date);
            const newDate = new Date(targetEntry.date);

            // Toggle between day (08:00) and night (20:00)
            if (currentDate.getHours() >= 18) {
                // Currently night time, switch to day
                newDate.setHours(8, 0, 0, 0);
            } else {
                // Currently day time, switch to night
                newDate.setHours(20, 0, 0, 0);
            }

            const updatedEntry = {
                id: targetEntry.id,
                executorId: targetEntry.user_id,
                projectId: targetEntry.afas_project_id,
                date: newDate.toISOString(),
                activity: targetEntry.activity,
            };

            // Check if there are subsequent entries in the chain to update
            const chainIndex = targetEntry.chain ? targetEntry.chain.findIndex(c => c.id === targetEntry.id) : -1;
            if (chainIndex !== -1 && chainIndex !== targetEntry.chain.length - 1) {
                const nextChainEntries = targetEntry.chain.slice(chainIndex + 1);
                this.showChainUpdateDialogForDayNightToggle(targetEntry, nextChainEntries, updatedEntry, newDate);
            } else {
                this.subscriptions.add(this.executorPlanningService.updatePlanning(updatedEntry).subscribe());
            }
        }
    }

    private copyPreviousWeekPlanning(executor: User, project: Project, clickedDate: string): void {
        const planningData = this.planningDataSubject.getValue();
        if (!planningData) return;

        // Get the clicked week's Monday
        const clickedDateObj = new Date(clickedDate);
        const dayOfWeek = clickedDateObj.getDay();
        const mondayOfClickedWeek = new Date(clickedDateObj);
        mondayOfClickedWeek.setDate(clickedDateObj.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

        // Get the previous week's Monday
        const mondayOfPrevWeek = new Date(mondayOfClickedWeek);
        mondayOfPrevWeek.setDate(mondayOfClickedWeek.getDate() - 7);

        // Get all planning entries for the executor and project from the previous week
        const prevWeekEntries = planningData.filter(entry => {
            if (entry.user_id !== executor.id || entry.afas_project_id !== project.afas_project_id) {
                return false;
            }

            const entryDate = new Date(entry.date);
            const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
            const mondayOnly = new Date(
                mondayOfPrevWeek.getFullYear(),
                mondayOfPrevWeek.getMonth(),
                mondayOfPrevWeek.getDate()
            );
            const sundayOfPrevWeek = new Date(mondayOnly);
            sundayOfPrevWeek.setDate(mondayOnly.getDate() + 6);

            return entryDateOnly >= mondayOnly && entryDateOnly <= sundayOfPrevWeek;
        });

        if (prevWeekEntries.length === 0) {
            return; // No previous week planning found
        }

        // Create new entries for the clicked week based on previous week
        const newEntries = prevWeekEntries.map(prevEntry => {
            const prevEntryDate = new Date(prevEntry.date);
            const daysDiff = Math.floor((prevEntryDate.getTime() - mondayOfPrevWeek.getTime()) / (1000 * 60 * 60 * 24));

            const newEntryDate = new Date(mondayOfClickedWeek);
            newEntryDate.setDate(mondayOfClickedWeek.getDate() + daysDiff);
            newEntryDate.setHours(prevEntryDate.getHours(), prevEntryDate.getMinutes(), 0, 0);

            return {
                executorId: executor.id,
                projectId: project.afas_project_id,
                date: newEntryDate.toISOString(),
                activity: prevEntry.activity,
            };
        });

        // Create all new planning entries
        newEntries.forEach(newEntry => {
            this.subscriptions.add(this.executorPlanningService.createPlanning(newEntry).subscribe());
        });
    }

    /**
     * Get all dates between two dates (inclusive)
     */
    private getDateRangeFromTo(startDate: string, endDate: string): string[] {
        const dates: string[] = [];
        const start = new Date(startDate);
        const end = new Date(endDate);

        start.setDate(start.getDate() + 1); // Start from the next day to avoid including the start date itself

        if (start > end) {
            return []; // Invalid range
        }

        const current = new Date(start);
        while (current <= end) {
            dates.push(current.toISOString().slice(0, 10));
            current.setDate(current.getDate() + 1);
        }

        return dates;
    }

    /**
     * Add bulk planning entries for multiple dates
     */
    private addBulkPlanningEntries(
        executor: User,
        project: Project,
        sourceEntry: ProjectExecutorPlanningEntry,
        dateRange: string[]
    ): void {
        const executorSchedule = this.getExecutorScheduleById(executor.id);
        if (!executorSchedule) {
            return;
        }

        const projectSchedule = executorSchedule.projects.find(p => p.afasProjectId === project.afas_project_id);
        if (!projectSchedule) {
            return;
        }

        // Filter out dates that already have planning entries
        const availableDates = dateRange.filter(date => {
            const existingEntry = projectSchedule.planningByDate[date];
            if (existingEntry) {
                return false;
            }
            return true;
        });

        if (availableDates.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen nieuwe planning',
                    text: 'Alle geselecteerde datums hebben al planning.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        // Create bulk requests for the API
        const bulkRequests: CreateExecutorPlanningRequest[] = availableDates.map(date => ({
            executorId: executor.id,
            projectId: project.afas_project_id,
            date: this.combineDateWithTime(date, sourceEntry.date),
            activity: sourceEntry.activity,
        }));

        // Save all entries to the backend at once
        this.subscriptions.add(
            this.executorPlanningService.createBulkPlanning(bulkRequests).subscribe({
                next: savedEntries => {
                    const currentPlanningData = this.planningDataSubject.getValue() || [];
                    const existingPlanningIds = new Set(currentPlanningData.map(entry => entry.id));
                    const uniqueSavedEntries = savedEntries.filter(
                        savedEntry => !existingPlanningIds.has(savedEntry.id)
                    );

                    // Update the local data structure with the saved entries
                    uniqueSavedEntries.forEach(savedEntry => {
                        const dateKey = savedEntry.date.substring(0, 10);

                        // Add to project schedule
                        if (!projectSchedule.planningEntries.some(entry => entry.id === savedEntry.id)) {
                            projectSchedule.planningEntries.push(savedEntry);
                        }
                        projectSchedule.planningByDate[dateKey] = savedEntry;

                        // Update the cell for this date
                        const cell = projectSchedule.cells.find(c => c.date === dateKey);
                        if (cell) {
                            cell.entry = savedEntry;
                            cell.hasEntry = true;
                            cell.duplicateEntries = this.getUniqueEntriesForDate(
                                projectSchedule.planningEntries,
                                dateKey
                            );
                            cell.hasDuplicate = cell.duplicateEntries.length > 1;
                            cell.activityLetter = savedEntry.activity[0]?.toUpperCase() || '';
                            cell.cssClasses = [
                                this.isWeekend(dateKey) ? 'weekend-cell' : '',
                                this.getTimeColorClass(savedEntry),
                            ].filter(cls => cls !== '');
                            cell.chain = this.findCellChain(projectSchedule, dateKey);
                        }
                    });

                    // Update chains for all cells in the project schedule since chains might have changed
                    projectSchedule.cells.forEach(cell => {
                        if (cell.hasEntry) {
                            cell.chain = this.findCellChain(projectSchedule, cell.date);
                        }
                    });

                    // Update the planning data subject to trigger UI refresh
                    this.planningDataSubject.next([...currentPlanningData, ...uniqueSavedEntries]);

                    // Update executor schedule subject
                    this.executorScheduleSubject.next([...this.executorScheduleArray]);
                },
                error: () => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij toevoegen',
                            text: 'Er is een fout opgetreden bij het toevoegen van de planning. Probeer het opnieuw.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private combineDateWithTime(dateString: string, sourceDateTimeString: string): string {
        const sourceDateTime = new Date(sourceDateTimeString);
        const targetDate = new Date(dateString);

        targetDate.setHours(sourceDateTime.getHours());
        targetDate.setMinutes(sourceDateTime.getMinutes());
        targetDate.setSeconds(sourceDateTime.getSeconds());

        return targetDate.toISOString();
    }

    private handleAddProject(result: SelectProjectDialogResult): void {
        const projectToAdd = result.project;
        projectToAdd.performer_id = result.executorId;

        const executorSchedule = this.getExecutorScheduleById(result.executorId);
        if (!executorSchedule) {
            return;
        }

        const projectSchedule = executorSchedule.projects.find(p => p.afasProjectId === projectToAdd.afas_project_id);
        if (projectSchedule) {
            return;
        }

        // Add to cache
        let executorCachedProjects = this.addedProjectsCache.get(result.executorId);
        if (!executorCachedProjects) {
            executorCachedProjects = new Map<string, Project>();
            this.addedProjectsCache.set(result.executorId, executorCachedProjects);
        }
        if (!executorCachedProjects.has(projectToAdd.afas_project_id)) {
            executorCachedProjects.set(projectToAdd.afas_project_id, projectToAdd);
        }
        this.projects.push(projectToAdd);

        const showProjectNumber = this.showProjectNumberSubject.getValue();

        const newProjectSchedule: ProjectSchedule = {
            project: projectToAdd,
            afasProjectId: projectToAdd.afas_project_id,
            planningEntries: [],
            planningByDate: {},
            cells: this.visibleDates.map(date => {
                const formattedDate = Utils.formatDate(date, 'EEE d MMM yyyy', 'nl');
                let title = formattedDate;

                // Add project number to title if enabled
                if (showProjectNumber && projectToAdd.afas_project_id) {
                    title = `${projectToAdd.afas_project_id}\n${title}`;
                }

                return {
                    date,
                    entry: null,
                    hasEntry: false,
                    hasDuplicate: false,
                    duplicateEntries: [],
                    isWeekend: this.isWeekend(date),
                    cssClasses: [this.isWeekend(date) ? 'weekend-cell' : ''],
                    hasAsphalt: false,
                    hasLeave: this.hasLeaveOnDate(executorSchedule.executor.id, date),
                    activityLetter: '',
                    chain: [], // Empty chain for new cells
                    title: title,
                    isFrezenAsfalt: false,
                    asphaltPlanningId: null,
                    asphaltIndicatorLetter: 'A',
                };
            }),
        };

        executorSchedule.projects.push(newProjectSchedule);

        this.executorScheduleSubject.next([...this.executorScheduleArray]);
    }

    trackByExecutorId(index: number, executorSchedule: ExecutorSchedule): number {
        return executorSchedule.executor.id;
    }

    trackByProjectId(index: number, projectSchedule: ProjectSchedule): string {
        return projectSchedule.afasProjectId;
    }

    trackByWeek(index: number, week: { number: number; dates: string[] }): number {
        return week.number;
    }

    trackByCell(index: number, cell: PlanningCell): string {
        return cell.date;
    }

    trackByPmId(_: number, pm: { id: number }) {
        return pm.id;
    }

    onCellRightClick(event: MouseEvent) {
        event.preventDefault();

        const resolved = this.resolveCellContextFromEvent(event);
        if (!resolved) {
            this.blockContextMenuEvent(event);
            return;
        }

        let { executor, project, date, cell } = resolved;

        project = resolved.project;
        executor = resolved.executor;
        date = resolved.date;
        cell = resolved.cell;

        if (!AuthorisationService.hasFunctionality(this.Functionalities.ExecutorPlanning)) {
            this.blockContextMenuEvent(event);
            return;
        }
        // Only allow right-click on asphalt cells if they have duplicates
        if ((cell.hasAsphalt && !cell.hasDuplicate) || (cell.isFrezenAsfalt && !cell.hasDuplicate)) {
            this.blockContextMenuEvent(event);
            return;
        }
        if (cell.hasDuplicate) {
            this.blockContextMenuEvent(event);
            this.openDuplicateSelectDialog(cell, executor, project);
            return;
        }
        if (AuthorisationService.hasFunctionality(this.Functionalities.ExecutorPlanning)) {
            const hasEntry = this.hasPlanning(project, executor, date);
            const hasChain = this.hasChain(project, executor, date);
            // Only get night mode from existing entry if there is one, otherwise preserve current state
            const isNightMode = hasEntry ? this.isNight(project, executor, date) : this.contextMenuData.isNightMode;
            this.contextMenuData = {
                executor: executor,
                project: project,
                date: date,
                hasEntry: hasEntry,
                isChain: hasChain,
                isNightMode: isNightMode,
            };
            // Open menu programmatically only when appropriate
            this.openContextMenu(event);
        }
    }

    private blockContextMenuEvent(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
    }

    private openContextMenu(event: MouseEvent): void {
        // Open menu and position it at mouse click location with viewport detection
        if (this.contextMenuTrigger) {
            this.contextMenuTrigger.openMenu();

            // Position menu at mouse coordinates after it's opened
            setTimeout(() => {
                const menuPanel = document.querySelector('.mat-mdc-menu-panel') as HTMLElement;
                if (menuPanel) {
                    const menuRect = menuPanel.getBoundingClientRect();
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;

                    let left = event.clientX;
                    let top = event.clientY;

                    // Check if menu would overflow on the right, position to the left instead
                    if (left + menuRect.width > viewportWidth) {
                        left = Math.max(0, event.clientX - menuRect.width);
                    }

                    // Check if menu would overflow at the bottom, position above instead
                    if (top + menuRect.height > viewportHeight) {
                        top = Math.max(0, event.clientY - menuRect.height);
                    }

                    menuPanel.style.position = 'fixed';
                    menuPanel.style.left = left + 'px';
                    menuPanel.style.top = top + 'px';
                }
            }, 0);
        }
    }

    private resolveCellContextFromEvent(
        event: MouseEvent
    ): { executor: User; project: Project; date: string; cell: PlanningCell } | null {
        const eventTarget = event.target as HTMLElement | null;
        const cellElement = eventTarget?.closest('.flex-cell') as HTMLElement | null;

        if (!cellElement) {
            return null;
        }

        const date = cellElement.getAttribute('data-date');
        const executorId = cellElement.getAttribute('data-executor-id');
        const projectId = cellElement.getAttribute('data-project-id');

        if (!date || !executorId || !projectId) {
            return null;
        }

        const parsedExecutorId = Number(executorId);
        const executor = this.getExecutorById(parsedExecutorId);
        const project = this.getProjectById(projectId);
        const cell = this.getCellByAttributes(parsedExecutorId, projectId, date);

        if (!executor || !project || !cell) {
            return null;
        }

        return {
            executor,
            project,
            date,
            cell,
        };
    }

    onContextMenuAction(action: string, activityType?: string, timeType?: string, hasEntry?: boolean) {
        if (!this.contextMenuData.executor || !this.contextMenuData.project) {
            return;
        }

        switch (action) {
            case 'delete':
                this.deletePlanning();
                break;
            case 'add_activity':
                if (activityType) {
                    // Use the toggle state to determine time type
                    const selectedTimeType = this.contextMenuData.isNightMode ? 'night' : 'day';
                    if (!hasEntry) {
                        this.addActivityPlanning(activityType, selectedTimeType);
                    } else {
                        this.editPlanning(activityType, selectedTimeType);
                    }
                }
                break;
            case 'delete_chain':
                this.deleteChain();
                break;
            case 'edit_chain':
                if (activityType) {
                    const selectedTimeType = this.contextMenuData.isNightMode ? 'night' : 'day';
                    this.editEntireChain(activityType, selectedTimeType);
                }
                break;
            case 'delete_future':
                this.deleteFuturePlanning();
                break;
            case 'move_future':
                this.moveFuturePlanning();
                break;
            case 'copy_to_executor':
                this.copyPlanningToExecutor();
                break;
            case 'shift_date_range':
                this.shiftDateRange();
                break;
        }
    }

    onTimeToggleChange(event: any) {
        this.contextMenuData.isNightMode = event.checked;

        // If there's an active context with an existing entry, update its time
        if (this.contextMenuData && this.contextMenuData.hasEntry) {
            this.updateExistingEntryTime();
        }
    }

    onShowFullYearChange(event: any) {
        this.showFullYearSubject.next(event.checked);
    }

    onShowProjectNumberChange(event: any) {
        this.showProjectNumberSubject.next(event.checked);
    }

    onProjectManagerFilterChange(projectManagerId: number | null) {
        this.selectedProjectManagerId = projectManagerId;
        this.selectedProjectManagerSubject.next(projectManagerId);
    }

    onViewOptionsChange(options: string[]) {
        this.selectedViewOptions = options;
        this.saveViewOptions(options);

        // Update hide unplanned executors
        const shouldHideUnplanned = options.includes('hideUnplanned');
        if (this.hideUnplannedExecutorsSubject.value !== shouldHideUnplanned) {
            this.hideUnplannedExecutorsSubject.next(shouldHideUnplanned);
        }

        // Update show full year
        const shouldShowFullYear = options.includes('showFullYear');
        if (this.showFullYearSubject.value !== shouldShowFullYear) {
            this.showFullYearSubject.next(shouldShowFullYear);
        }

        // Update show previous weeks
        const shouldShowPreviousWeeks = options.includes('showPreviousWeeks');
        if (this.showPreviousWeeksSubject.value !== shouldShowPreviousWeeks) {
            this.showPreviousWeeksSubject.next(shouldShowPreviousWeeks);
        }

        // Update show project number
        const shouldShowProjectNumber = options.includes('showProjectNumber');
        if (this.showProjectNumberSubject.value !== shouldShowProjectNumber) {
            this.showProjectNumberSubject.next(shouldShowProjectNumber);
        }

        // Update expand all
        const shouldExpandAll = options.includes('expandAll');
        const executorScheduleArray = this.executorScheduleSubject.getValue();
        if (executorScheduleArray) {
            executorScheduleArray.forEach(executorSchedule => {
                executorSchedule.collapsed = !shouldExpandAll;
            });
            this.executorScheduleSubject.next([...executorScheduleArray]);
            this.updateExpandAllState();
        }
    }

    toggleExecutorCollapse(executorId: number): void {
        const executorScheduleArray = this.executorScheduleSubject.getValue();
        if (!executorScheduleArray) return;

        const executorSchedule = executorScheduleArray.find(es => es.executor.id === executorId);
        if (executorSchedule) {
            executorSchedule.collapsed = !executorSchedule.collapsed;
            // Trigger change detection
            this.executorScheduleSubject.next([...executorScheduleArray]);
            // Update the expand all state
            this.updateExpandAllState();
        }
    }

    onExpandAllExecutorsChange(event: any): void {
        const executorScheduleArray = this.executorScheduleSubject.getValue();
        if (!executorScheduleArray) return;

        const shouldExpand = event.checked;

        // Set all executors to the same collapsed state
        executorScheduleArray.forEach(executorSchedule => {
            executorSchedule.collapsed = !shouldExpand;
        });

        // Trigger change detection
        this.executorScheduleSubject.next([...executorScheduleArray]);

        // Update the expand all state
        this.updateExpandAllState();
    }

    private updateExpandAllState(): void {
        const executorScheduleArray = this.executorScheduleSubject.getValue();
        if (!executorScheduleArray || executorScheduleArray.length === 0) {
            this.allExecutorsExpandedState = { checked: false, indeterminate: false };
            return;
        }

        const expandedCount = executorScheduleArray.filter(es => !es.collapsed).length;
        const totalCount = executorScheduleArray.length;

        if (expandedCount === 0) {
            // All collapsed
            this.allExecutorsExpandedState = { checked: false, indeterminate: false };
        } else if (expandedCount === totalCount) {
            // All expanded
            this.allExecutorsExpandedState = { checked: true, indeterminate: false };
        } else {
            // Mixed state
            this.allExecutorsExpandedState = { checked: false, indeterminate: true };
        }
    }

    private updateExistingEntryTime() {
        const { project, executor, date } = this.contextMenuData;
        if (!project || !executor || !date) return;

        // Find the existing planning entry
        const executorSchedule = this.executorScheduleArray.find(es => es.executor.id === executor.id);
        if (!executorSchedule) return;

        const projectSchedule = executorSchedule.projects.find(
            ps => ps.project.afas_project_id === project.afas_project_id
        );
        if (!projectSchedule) return;

        const existingEntry = projectSchedule.planningByDate[date];
        if (!existingEntry) return;

        // Determine the new time based on the toggle state
        const selectedTimeType = this.contextMenuData.isNightMode ? 'night' : 'day';
        const timeOption = this.timeOptions.find(t => t.value === selectedTimeType);
        if (!timeOption) return;

        // Update the entry's time
        const updatedDate = new Date(date);
        updatedDate.setHours(timeOption.hour, 0, 0, 0);

        const updatedEntry: UpdateExecutorPlanningRequest = {
            id: existingEntry.id,
            executorId: executor.id,
            projectId: project.afas_project_id,
            date: updatedDate.toISOString(),
            activity: existingEntry.activity,
        };

        // Update the entry on the server
        this.subscriptions.add(
            this.executorPlanningService.updatePlanning(updatedEntry).subscribe({
                next: () => {
                    // Update the local data
                    existingEntry.date = updatedDate.toISOString();

                    // Trigger change detection
                    this.executorScheduleSubject.next(this.executorScheduleArray);
                },
                error: error => {
                    console.error('Failed to update planning entry time:', error);
                },
            })
        );
    }

    private addActivityPlanning(activityType: string, timeType: string) {
        const { project, executor, date } = this.contextMenuData;

        const timeOption = this.timeOptions.find(t => t.value === timeType);
        const activity = this.activities.find(a => a.value === activityType);

        if (!timeOption || !activity) return;

        // add timeoption to the date
        const dateWithTime = new Date(date);
        dateWithTime.setHours(timeOption.hour, 0, 0, 0);

        const planningData: CreateExecutorPlanningRequest = {
            executorId: executor.id,
            projectId: project.afas_project_id,
            date: dateWithTime.toISOString(),
            activity: activityType,
        };

        this.executorPlanningService.createPlanning(planningData).subscribe({
            next: response => {
                this.loadInitialData();
                // Keep isNightMode persistent - don't reset to false
            },
        });
    }

    private editPlanning(activityType?: string, timeType?: string) {
        const { executor, project, date } = this.contextMenuData;
        if (!executor || !project) return;
        const planningEntry = this.getPlanningEntry(executor.id, project.afas_project_id, date);

        if (!planningEntry) {
            return;
        }

        const timeOption = this.timeOptions.find(t => t.value === timeType);
        if (!timeOption) {
            return;
        }

        const dateWithTime = new Date(date);
        dateWithTime.setHours(timeOption.hour, 0, 0, 0);

        const record: UpdateExecutorPlanningRequest = {
            id: planningEntry.id,
            executorId: executor.id,
            projectId: project.afas_project_id,
            date: dateWithTime.toISOString(),
            activity: activityType || planningEntry.activity,
        };

        // find index of the current id in the planningEntry.chain
        const chainIndex = planningEntry.chain ? planningEntry.chain.findIndex(c => c.id === planningEntry.id) : -1;
        if (chainIndex !== -1 && chainIndex !== planningEntry.chain.length - 1) {
            const nextChainEntries = planningEntry.chain.slice(chainIndex + 1);

            this.showChainUpdateDialog(planningEntry, nextChainEntries, record, activityType, dateWithTime);
        } else {
            this.sendEditRequest(record);
        }

        // Keep isNightMode persistent - don't reset to false
    }

    private showChainUpdateDialog(
        planningEntry: ProjectExecutorPlanningEntry,
        nextChainEntries: ChainEntry[],
        currentRecord: UpdateExecutorPlanningRequest,
        activityType: string,
        dateWithTime: Date
    ) {
        const firstDate = new Date(nextChainEntries[0].date);
        const lastDate = new Date(nextChainEntries[nextChainEntries.length - 1].date);

        const currentDateFormatted = Utils.formatDate(planningEntry.date, 'd MMMM', 'nl');
        const firstDateFormatted = Utils.formatDate(firstDate, 'd MMMM', 'nl');
        const lastDateFormatted = Utils.formatDate(lastDate, 'd MMMM', 'nl');

        const message =
            nextChainEntries.length === 1
                ? `Wil je ook ${firstDateFormatted} aanpassen?`
                : `Wil je ${firstDateFormatted} t/m ${lastDateFormatted} ook aanpassen?`;

        this.confirmDialogService
            .confirm({
                title: 'Opvolgende dagen aanpassen',
                text: message,
                confirm: 'Ja, alle dagen',
                cancel: `Alleen ${currentDateFormatted}`,
            })
            .then(confirmed => {
                // User chose "Yes" - update current + following entries
                this.updateCurrentAndFollowingEntries(planningEntry, nextChainEntries, activityType, dateWithTime);
            })
            .catch(() => {
                // User chose "No" - update only current entry
                this.sendEditRequest(currentRecord);
            });
    }

    private updateCurrentAndFollowingEntries(
        planningEntry: ProjectExecutorPlanningEntry,
        nextChainEntries: ChainEntry[],
        activityType: string,
        dateWithTime: Date
    ) {
        // Create bulk update requests for current + following chain entries
        const bulkUpdateRequests: BulkUpdateExecutorPlanningRequest[] = [];

        // Add current entry
        bulkUpdateRequests.push({
            id: planningEntry.id,
            date: dateWithTime.toISOString(),
            activity: activityType,
        });

        // Add following entries
        nextChainEntries.forEach(entry => {
            bulkUpdateRequests.push({
                id: entry.id,
                date: this.combineDateWithTime(entry.date, dateWithTime.toISOString()),
                activity: activityType,
            });
        });

        this.subscriptions.add(
            this.executorPlanningService.updateBulkPlanning(bulkUpdateRequests).subscribe({
                next: response => {
                    this.loadInitialData();
                },
                error: error => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij bijwerken',
                            text: 'Er is een fout opgetreden bij het bijwerken van de planning. Probeer het opnieuw.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private showChainUpdateDialogForDayNightToggle(
        planningEntry: ProjectExecutorPlanningEntry,
        nextChainEntries: ChainEntry[],
        currentRecord: any,
        newDate: Date
    ) {
        const firstDate = new Date(nextChainEntries[0].date);
        const lastDate = new Date(nextChainEntries[nextChainEntries.length - 1].date);

        const currentDateFormatted = Utils.formatDate(planningEntry.date, 'd MMMM', 'nl');
        const firstDateFormatted = Utils.formatDate(firstDate, 'd MMMM', 'nl');
        const lastDateFormatted = Utils.formatDate(lastDate, 'd MMMM', 'nl');

        const timeLabel = newDate.getHours() >= 18 ? 'nacht' : 'dag';
        const message =
            nextChainEntries.length === 1
                ? `Wil je ook ${firstDateFormatted} naar ${timeLabel} zetten?`
                : `Wil je ${firstDateFormatted} t/m ${lastDateFormatted} ook naar ${timeLabel} zetten?`;

        this.confirmDialogService
            .confirm({
                title: 'Opvolgende dagen aanpassen',
                text: message,
                confirm: 'Ja, alle dagen',
                cancel: `Alleen ${currentDateFormatted}`,
            })
            .then(confirmed => {
                // User chose "Yes" - update current + following entries to day/night
                this.updateCurrentAndFollowingEntriesForDayNight(planningEntry, nextChainEntries, newDate);
            })
            .catch(() => {
                // User chose "No" - update only current entry
                this.subscriptions.add(this.executorPlanningService.updatePlanning(currentRecord).subscribe());
            });
    }

    private updateCurrentAndFollowingEntriesForDayNight(
        planningEntry: ProjectExecutorPlanningEntry,
        nextChainEntries: ChainEntry[],
        newDate: Date
    ) {
        // Create bulk update requests for current + following chain entries
        const bulkUpdateRequests: BulkUpdateExecutorPlanningRequest[] = [];

        // Add current entry
        bulkUpdateRequests.push({
            id: planningEntry.id,
            date: newDate.toISOString(),
            activity: planningEntry.activity,
        });

        // Add following entries with same time change
        nextChainEntries.forEach(entry => {
            const entryNewDate = new Date(entry.date);
            entryNewDate.setHours(newDate.getHours(), newDate.getMinutes(), 0, 0);

            // Find the original planning entry to get the activity
            const originalEntry = this.getPlanningEntry(
                planningEntry.user_id,
                planningEntry.afas_project_id,
                entry.date
            );

            bulkUpdateRequests.push({
                id: entry.id,
                date: entryNewDate.toISOString(),
                activity: originalEntry?.activity || planningEntry.activity,
            });
        });

        this.subscriptions.add(
            this.executorPlanningService.updateBulkPlanning(bulkUpdateRequests).subscribe({
                next: response => {
                    this.loadInitialData();
                },
                error: error => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij bijwerken',
                            text: 'Er is een fout opgetreden bij het bijwerken van de dag/nacht planning. Probeer het opnieuw.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private openDuplicateSelectDialog(cell: PlanningCell, executor: User, project: Project): void {
        const data: DuplicateEntrySelectDialogData = {
            entries: cell.duplicateEntries,
            projectName: project.name,
            date: cell.date,
        };
        this.dialog
            .open(DuplicateEntrySelectDialogComponent, {
                width: '480px',
                data,
                disableClose: false,
            })
            .afterClosed()
            .subscribe((result: DuplicateEntrySelectDialogResult | null) => {
                if (!result) return;
                if (result.action === 'delete') {
                    this.deletePlanningEntry(result.entry);
                } else if (result.action === 'move_date') {
                    this.movePlanningEntryDate(result.entry, result.newDate);
                }
            });
    }

    private deletePlanningEntry(entry: ProjectExecutorPlanningEntry): void {
        this.subscriptions.add(
            this.executorPlanningService.deletePlanning(entry.id).subscribe({
                next: () => this.loadInitialData(),
            })
        );
    }

    private movePlanningEntryDate(entry: ProjectExecutorPlanningEntry, newDate: Date): void {
        const originalDate = new Date(entry.date);
        newDate.setHours(originalDate.getHours(), originalDate.getMinutes(), 0, 0);
        const record: UpdateExecutorPlanningRequest = {
            id: entry.id,
            executorId: entry.user_id,
            projectId: entry.afas_project_id,
            date: newDate.toISOString(),
            activity: entry.activity,
        };
        this.sendEditRequest(record);
    }

    private sendEditRequest(record: UpdateExecutorPlanningRequest) {
        this.subscriptions.add(
            this.executorPlanningService.updatePlanning(record).subscribe({
                next: response => {
                    this.loadInitialData();
                },
            })
        );
    }

    private deletePlanning() {
        const { executor, project, date } = this.contextMenuData;
        if (!executor || !project) return;

        const planningEntry = this.getPlanningEntry(executor.id, project.afas_project_id, date);

        this.subscriptions.add(
            this.executorPlanningService.deletePlanning(planningEntry.id).subscribe({
                next: response => {
                    this.loadInitialData();
                },
            })
        );
    }

    private deleteFuturePlanning() {
        const { executor, project, date } = this.contextMenuData;
        if (!executor || !project) return;

        const projectSchedule = this.getProjectScheduleForExecutor(executor.id, project.afas_project_id);
        if (!projectSchedule) return;

        // Find all planning entries from the selected date onwards
        const selectedDate = new Date(date);
        const futurePlanningEntries = projectSchedule.planningEntries.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= selectedDate;
        });

        if (futurePlanningEntries.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen toekomstige planning',
                    text: 'Er zijn geen planningen gevonden vanaf de geselecteerde datum.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        const dateString = Utils.formatDate(date, 'd MMMM yyyy', 'nl');
        const entryCount = futurePlanningEntries.length;
        const message = `Weet je zeker dat je ${entryCount === 1 ? 'de' : `alle ${entryCount}`} planning${entryCount === 1 ? '' : 'en'} vanaf ${dateString} wilt verwijderen?`;

        this.confirmDialogService
            .confirm({
                title: 'Verwijder toekomstige planning',
                text: message,
                confirm: 'Verwijderen',
                cancel: 'Annuleren',
            })
            .then(confirmed => {
                const entryIds = futurePlanningEntries.map(entry => entry.id);

                this.subscriptions.add(
                    this.executorPlanningService.deleteBulkPlanning(entryIds).subscribe({
                        next: response => {
                            this.loadInitialData();
                        },
                        error: error => {
                            this.confirmDialogService
                                .confirm({
                                    title: 'Fout bij verwijderen',
                                    text: 'Er is een fout opgetreden bij het verwijderen van de toekomstige planning. Probeer het opnieuw.',
                                    confirm: 'Sluiten',
                                    cancel: null,
                                })
                                .catch(() => {});
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    private moveFuturePlanning() {
        const { executor, project, date } = this.contextMenuData;
        if (!executor || !project) return;

        const projectSchedule = this.getProjectScheduleForExecutor(executor.id, project.afas_project_id);
        if (!projectSchedule) return;

        // Find all planning entries from the selected date onwards
        const selectedDate = new Date(date);
        const futurePlanningEntries = projectSchedule.planningEntries.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= selectedDate;
        });

        if (futurePlanningEntries.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen toekomstige planning',
                    text: 'Er zijn geen planningen gevonden vanaf de geselecteerde datum.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        // Open dialog to select target executor and project
        this.showExecutorOrProjectChoiceDialog(futurePlanningEntries, executor, project);
    }

    private showExecutorOrProjectChoiceDialog(
        futurePlanningEntries: ProjectExecutorPlanningEntry[],
        sourceExecutor: User,
        sourceProject: Project
    ) {
        const entryCount = futurePlanningEntries.length;

        const dialogData: ExecutorProjectChoiceDialogData = {
            entryCount: entryCount,
            sourceExecutor: sourceExecutor,
            sourceProject: sourceProject,
        };

        this.dialog
            .open(ExecutorProjectChoiceDialogComponent, {
                width: '500px',
                data: dialogData,
                disableClose: false, // Allow clicking outside to close
            })
            .afterClosed()
            .subscribe((result: ExecutorProjectChoiceDialogResult) => {
                if (result) {
                    if (result.choice === 'executor') {
                        // User chose to change executor
                        this.proceedWithExecutorChange(futurePlanningEntries, sourceExecutor, sourceProject);
                    } else if (result.choice === 'project') {
                        // User chose to change project only (keep same executor)
                        this.showProjectSelectionForMove(futurePlanningEntries, sourceExecutor, sourceProject);
                    }
                }
                // If result is null/undefined, user cancelled (clicked outside or close button)
            });
    }

    private proceedWithExecutorChange(
        futurePlanningEntries: ProjectExecutorPlanningEntry[],
        sourceExecutor: User,
        sourceProject: Project
    ) {
        // Create a list of all available executors
        const availableExecutors = this.users.filter(u => u.id !== sourceExecutor.id);

        if (availableExecutors.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen andere uitvoerders',
                    text: 'Er zijn geen andere uitvoerders beschikbaar om naar te verplaatsen.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        // Show executor dropdown selection dialog
        this.openExecutorSelectionDialog(futurePlanningEntries, sourceExecutor, sourceProject, availableExecutors);
    }

    private openExecutorSelectionDialog(
        futurePlanningEntries: ProjectExecutorPlanningEntry[],
        sourceExecutor: User,
        sourceProject: Project,
        availableExecutors: User[]
    ) {
        const dialogData: SelectExecutorDialogData = {
            title: 'Selecteer doeluitvoerder',
            message: `Kies een uitvoerder om de planning naartoe te verplaatsen vanaf ${sourceExecutor.name}.\nProject blijft: ${sourceProject.name}`,
            confirmButtonText: 'Verplaatsen',
            availableExecutors: availableExecutors,
            sourceExecutor: sourceExecutor,
        };

        this.dialog
            .open(SelectExecutorDialogComponent, {
                width: '500px',
                data: dialogData,
                disableClose: false,
            })
            .afterClosed()
            .subscribe((result: SelectExecutorDialogResult) => {
                if (result && result.selectedExecutor) {
                    // When changing executor, keep the same project
                    this.executeMoveOperation(
                        futurePlanningEntries,
                        result.selectedExecutor,
                        sourceProject,
                        sourceExecutor,
                        'executor'
                    );
                }
            });
    }

    private showProjectSelectionForMove(
        futurePlanningEntries: ProjectExecutorPlanningEntry[],
        keepExecutor: User,
        sourceProject: Project
    ) {
        // Get current project IDs for the executor to avoid duplicates
        const executorSchedule = this.getExecutorScheduleById(keepExecutor.id);
        const currentProjectIds = executorSchedule?.projects.map(p => p.afasProjectId) || [];

        const dialogData: SelectProjectDialogData = {
            executor: keepExecutor,
            currentProjectIds: currentProjectIds,
            buttonText: 'Selecteren',
        };

        this.dialog
            .open(SelectProjectDialogComponent, {
                width: '400px',
                data: dialogData,
                disableClose: false,
            })
            .afterClosed()
            .subscribe((result: SelectProjectDialogResult) => {
                if (result) {
                    // When changing project, keep the same executor - pass sourceProject for better messaging
                    this.executeMoveOperation(
                        futurePlanningEntries,
                        keepExecutor,
                        result.project,
                        keepExecutor,
                        'project',
                        sourceProject
                    );
                }
            });
    }

    private executeMoveOperation(
        futurePlanningEntries: ProjectExecutorPlanningEntry[],
        targetExecutor: User,
        targetProject: Project,
        sourceExecutor?: User,
        changeType?: 'executor' | 'project',
        sourceProject?: Project
    ) {
        if (changeType && sourceExecutor && sourceProject) {
            const executorChanged = sourceExecutor.id !== targetExecutor.id;
            const projectChanged = sourceProject.afas_project_id !== targetProject.afas_project_id;

            if (executorChanged && projectChanged) {
                console.error('Invalid move operation: Both executor and project cannot be changed at the same time');
                this.confirmDialogService
                    .confirm({
                        title: 'Ongeldige bewerking',
                        text: 'Er kan maar één ding tegelijk gewijzigd worden: óf de uitvoerder óf het project.',
                        confirm: 'Sluiten',
                        cancel: null,
                    })
                    .catch(() => {});
                return;
            }
        }

        const targetExecutorSchedule = this.getExecutorScheduleById(targetExecutor.id);
        const hasTargetProject = targetExecutorSchedule?.projects.some(
            p => p.afasProjectId === targetProject.afas_project_id
        );

        if (!hasTargetProject) {
            const result: SelectProjectDialogResult = {
                project: targetProject,
                executorId: targetExecutor.id,
            };
            this.handleAddProject(result);
        }

        const createRequests: CreateExecutorPlanningRequest[] = futurePlanningEntries.map(entry => ({
            executorId: targetExecutor.id,
            projectId: targetProject.afas_project_id,
            date: entry.date,
            activity: entry.activity,
        }));

        this.subscriptions.add(
            this.executorPlanningService.createBulkPlanning(createRequests).subscribe({
                next: newEntries => {
                    const originalEntryIds = futurePlanningEntries.map(entry => entry.id);

                    this.subscriptions.add(
                        this.executorPlanningService.deleteBulkPlanning(originalEntryIds).subscribe({
                            next: () => {
                                this.loadInitialData();

                                const entryCount = futurePlanningEntries.length;
                                let message: string;

                                if (changeType === 'executor') {
                                    message = `${entryCount} planning${entryCount === 1 ? '' : 'en'} succesvol verplaatst van ${sourceExecutor?.name} naar ${targetExecutor.name} (project: ${targetProject.name})`;
                                } else if (changeType === 'project') {
                                    // Use the sourceProject parameter if available, otherwise fallback to the entry's project ID
                                    const sourceProjectName =
                                        sourceProject?.name || futurePlanningEntries[0]?.afas_project_id;
                                    message = `${entryCount} planning${entryCount === 1 ? '' : 'en'} succesvol verplaatst van project ${sourceProjectName} naar ${targetProject.name} (uitvoerder: ${targetExecutor.name})`;
                                } else {
                                    message = `${entryCount} planning${entryCount === 1 ? '' : 'en'} succesvol verplaatst naar ${targetExecutor.name} (${targetProject.name})`;
                                }

                                this.confirmDialogService
                                    .confirm({
                                        title: 'Planning verplaatst',
                                        text: message,
                                        confirm: 'Sluiten',
                                        cancel: null,
                                    })
                                    .catch(() => {});
                            },
                            error: error => {
                                // If deletion fails, we should ideally rollback the creation
                                this.confirmDialogService
                                    .confirm({
                                        title: 'Fout bij verplaatsen',
                                        text: 'Er is een fout opgetreden bij het verwijderen van de originele planning. De nieuwe planning is wel aangemaakt.',
                                        confirm: 'Sluiten',
                                        cancel: null,
                                    })
                                    .catch(() => {});
                            },
                        })
                    );
                },
                error: error => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij verplaatsen',
                            text: 'Er is een fout opgetreden bij het aanmaken van de nieuwe planning.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private copyPlanningToExecutor() {
        const { executor, project, date } = this.contextMenuData;
        if (!executor || !project || !date) return;

        const planningEntry = this.getPlanningEntry(executor.id, project.afas_project_id, date);
        if (!planningEntry) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen planning gevonden',
                    text: 'Er is geen planning gevonden op de geselecteerde dag.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        const availableExecutors = this.users.filter(user => user.id !== executor.id);

        if (!availableExecutors.length) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen andere uitvoerders',
                    text: 'Er zijn geen andere uitvoerders beschikbaar om naar te kopieren.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        const projectSchedule = this.getProjectScheduleForExecutor(executor.id, project.afas_project_id);
        const chainEntries = projectSchedule ? this.findCellChain(projectSchedule, date) : [];
        const chainPlanningEntries = chainEntries
            .map(chainEntry => projectSchedule?.planningByDate[chainEntry.date])
            .filter((entry): entry is ProjectExecutorPlanningEntry => !!entry);

        const entriesToCopy = chainPlanningEntries.length ? chainPlanningEntries : [planningEntry];
        const hasChain = entriesToCopy.length > 1;

        const dialogData: SelectExecutorDialogData = {
            title: 'Kopieer planning naar uitvoerder',
            message: `Kies een uitvoerder om een kopie te maken van deze planning van ${executor.name}.\nProject blijft: ${project.name}`,
            confirmButtonText: 'Kopieren',
            availableExecutors,
            sourceExecutor: executor,
        };

        this.dialog
            .open(SelectExecutorDialogComponent, {
                width: '500px',
                data: dialogData,
                disableClose: false,
            })
            .afterClosed()
            .subscribe((result: SelectExecutorDialogResult) => {
                if (result?.selectedExecutor) {
                    if (!hasChain) {
                        this.executeCopyToExecutor(planningEntry, result.selectedExecutor, project);
                        return;
                    }

                    const chainMessage = `Deze planning is onderdeel van een reeks van ${entriesToCopy.length} dagen. Wil je de hele reeks kopieren naar ${result.selectedExecutor.name}?`;
                    this.confirmDialogService
                        .confirm({
                            title: 'Kopieer reeks',
                            text: chainMessage,
                            confirm: 'Hele reeks kopieren',
                            cancel: 'Alleen geselecteerde dag',
                        })
                        .then(() => {
                            this.executeCopyChainToExecutor(entriesToCopy, result.selectedExecutor, project);
                        })
                        .catch(() => {
                            this.executeCopyToExecutor(planningEntry, result.selectedExecutor, project);
                        });
                }
            });
    }

    private executeCopyToExecutor(planningEntry: ProjectExecutorPlanningEntry, targetExecutor: User, project: Project) {
        const targetExecutorSchedule = this.getExecutorScheduleById(targetExecutor.id);
        const hasTargetProject = targetExecutorSchedule?.projects.some(
            p => p.afasProjectId === project.afas_project_id
        );

        if (!hasTargetProject) {
            const addProjectResult: SelectProjectDialogResult = {
                project,
                executorId: targetExecutor.id,
            };
            this.handleAddProject(addProjectResult);
        }

        const copyRequest: CreateExecutorPlanningRequest = {
            executorId: targetExecutor.id,
            projectId: project.afas_project_id,
            date: planningEntry.date,
            activity: planningEntry.activity,
            allowDuplicates: true,
        };

        this.subscriptions.add(
            this.executorPlanningService.createPlanning(copyRequest).subscribe({
                next: () => {
                    this.loadInitialData();
                    this.confirmDialogService
                        .confirm({
                            title: 'Planning gekopieerd',
                            text: `De planning is gekopieerd naar ${targetExecutor.name}.`,
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
                error: () => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij kopieren',
                            text: 'Er is een fout opgetreden bij het kopieren van de planning.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private executeCopyChainToExecutor(
        planningEntries: ProjectExecutorPlanningEntry[],
        targetExecutor: User,
        project: Project
    ) {
        const targetExecutorSchedule = this.getExecutorScheduleById(targetExecutor.id);
        const hasTargetProject = targetExecutorSchedule?.projects.some(
            p => p.afasProjectId === project.afas_project_id
        );

        if (!hasTargetProject) {
            const addProjectResult: SelectProjectDialogResult = {
                project,
                executorId: targetExecutor.id,
            };
            this.handleAddProject(addProjectResult);
        }

        const copyRequests: CreateExecutorPlanningRequest[] = planningEntries.map(entry => ({
            executorId: targetExecutor.id,
            projectId: project.afas_project_id,
            date: entry.date,
            activity: entry.activity,
            allowDuplicates: true,
        }));

        this.subscriptions.add(
            this.executorPlanningService.createBulkPlanning(copyRequests).subscribe({
                next: () => {
                    this.loadInitialData();
                    const entryCount = planningEntries.length;
                    this.confirmDialogService
                        .confirm({
                            title: 'Reeks gekopieerd',
                            text: `${entryCount} planning${entryCount === 1 ? '' : 'en'} zijn gekopieerd naar ${targetExecutor.name}.`,
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
                error: () => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij kopieren',
                            text: 'Er is een fout opgetreden bij het kopieren van de reeks.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private shiftDateRange() {
        const { executor, project, date } = this.contextMenuData;
        if (!executor || !project || !date) return;

        const projectSchedule = this.getProjectScheduleForExecutor(executor.id, project.afas_project_id);
        if (!projectSchedule) return;

        // Find all planning entries from the selected date onwards
        const selectedDate = new Date(date);
        const futureEntries = projectSchedule.planningEntries.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= selectedDate;
        });

        if (futureEntries.length === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen planning gevonden',
                    text: 'Er is geen planning gevonden sinds de geselecteerde datum.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        const dialogData: ShiftDateRangeDialogData = {
            originalDate: date,
            project: project,
            executor: executor,
        };

        this.dialog
            .open(ShiftDateRangeDialogComponent, {
                width: '500px',
                data: dialogData,
                disableClose: false,
            })
            .afterClosed()
            .subscribe((result: ShiftDateRangeDialogResult) => {
                if (result?.newDate) {
                    this.executeShiftDateRange(futureEntries, date, result.newDate);
                }
            });
    }

    private executeShiftDateRange(
        futureEntries: ProjectExecutorPlanningEntry[],
        originalDate: string,
        newDate: string
    ) {
        const originalDateObj = new Date(originalDate);
        originalDateObj.setHours(0, 0, 0, 0);
        const newDateObj = new Date(newDate);
        newDateObj.setHours(0, 0, 0, 0);

        // Calculate day difference
        const millisecondsPerDay = DAY_IN_MILLISECONDS;
        const daysDifference = Math.round((newDateObj.getTime() - originalDateObj.getTime()) / millisecondsPerDay);

        if (daysDifference === 0) {
            this.confirmDialogService
                .confirm({
                    title: 'Geen verschil',
                    text: 'De nieuwe datum is gelijk aan de originele datum.',
                    confirm: 'Sluiten',
                    cancel: null,
                })
                .catch(() => {});
            return;
        }

        // Create bulk update requests for all future entries
        const bulkUpdateRequests: BulkUpdateExecutorPlanningRequest[] = futureEntries.map(entry => {
            const entryDate = new Date(entry.date);
            entryDate.setDate(entryDate.getDate() + daysDifference);

            return {
                id: entry.id,
                date: entryDate.toISOString(),
                allowDuplicates: true,
            };
        });

        this.subscriptions.add(
            this.executorPlanningService.updateBulkPlanning(bulkUpdateRequests).subscribe({
                next: () => {
                    this.loadInitialData();
                    const entryCount = futureEntries.length;
                    const direction = daysDifference > 0 ? 'naar voren' : 'naar achter';
                    const message = `${entryCount} planning${entryCount === 1 ? '' : 'en'} (${Math.abs(daysDifference)} dag${Math.abs(daysDifference) === 1 ? '' : 'en'}) ${direction} verschoven.`;
                    this.confirmDialogService
                        .confirm({ title: 'Planning verschoven', text: message, confirm: 'Sluiten', cancel: null })
                        .catch(() => {});
                },
                error: () => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Fout bij verschuiven',
                            text: 'Er is een fout opgetreden bij het verschuiven van de planning.',
                            confirm: 'Sluiten',
                            cancel: null,
                        })
                        .catch(() => {});
                },
            })
        );
    }

    private deleteChain() {
        const { executor, project, date } = this.contextMenuData;

        const dateString = Utils.formatDate(date, 'd MMMM', 'nl');

        this.confirmDialogService
            .confirm({
                title: 'Verwijder reeks',
                text: `Weet je zeker dat je de reeks wilt verwijderen voor ${executor.firstname} ${executor.lastname} op ${dateString}?`,
                confirm: 'Verwijderen',
                cancel: 'Annuleren',
            })
            .then(confirmed => {
                const projectSchedule = this.getProjectScheduleForExecutor(executor.id, project.afas_project_id);
                if (!projectSchedule) return;

                const chain = this.findCellChain(projectSchedule, date);
                const chainIds = chain.map(c => c.id);

                this.subscriptions.add(
                    this.executorPlanningService.deleteBulkPlanning(chainIds).subscribe({
                        next: response => {
                            this.loadInitialData();
                        },
                    })
                );
            });
    }

    private editEntireChain(activityType: string, timeType: string) {
        const { executor, project, date } = this.contextMenuData;

        const projectSchedule = this.getProjectScheduleForExecutor(executor.id, project.afas_project_id);
        if (!projectSchedule) return;

        const chain = this.findCellChain(projectSchedule, date);
        if (chain.length <= 1) {
            // If no chain or single entry, just edit the single entry
            this.editPlanning(activityType, timeType);
            return;
        }

        const timeOption = this.timeOptions.find(t => t.value === timeType);
        if (!timeOption) return;

        const activity = this.activities.find(a => a.value === activityType);
        if (!activity) return;

        this.confirmDialogService
            .confirm({
                title: 'Bewerk hele keten',
                text: `Weet je zeker dat je alle ${chain.length} planningen in de keten wilt wijzigen naar ${activity.label} (${timeOption.label})?`,
                confirm: 'Wijzigen',
                cancel: 'Annuleren',
            })
            .then(confirmed => {
                const bulkUpdateRequests: BulkUpdateExecutorPlanningRequest[] = chain.map(chainEntry => {
                    const dateWithTime = new Date(chainEntry.date);
                    dateWithTime.setHours(timeOption.hour, 0, 0, 0);

                    return {
                        id: chainEntry.id,
                        activity: activityType,
                        date: dateWithTime.toISOString(),
                    };
                });

                this.subscriptions.add(
                    this.executorPlanningService.updateBulkPlanning(bulkUpdateRequests).subscribe({
                        next: response => {
                            this.loadInitialData();
                        },
                        error: error => {
                            this.confirmDialogService
                                .confirm({
                                    title: 'Fout bij wijzigen',
                                    text: 'Er is een fout opgetreden bij het wijzigen van de keten. Probeer het opnieuw.',
                                    confirm: 'Sluiten',
                                    cancel: null,
                                })
                                .catch(() => {});
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    private loadViewOptions(): string[] {
        return this.LocalStorage.executorPlanningSettings.viewOptions;
    }

    private saveViewOptions(options: string[]): void {
        const settings: ExecutorPlanningSettings = {
            ...this.LocalStorage.executorPlanningSettings,
            viewOptions: options,
        };
        this.LocalStorage.executorPlanningSettings = settings;
    }

    private getEarliestPlanningDate(planningByDate: { [date: string]: any }): string | null {
        if (!planningByDate || Object.keys(planningByDate).length === 0) {
            return null;
        }

        // Get all dates that have planning entries and sort them
        const planningDates = Object.keys(planningByDate)
            .filter(date => planningByDate[date]) // Only include dates with actual planning entries
            .sort(); // Sort dates chronologically

        return planningDates.length > 0 ? planningDates[0] : null;
    }

    private hasMatchingExecutorActivity(
        asphaltEntry: AsphaltPerformerPlanningEntry,
        executorActivities: Set<string>
    ): boolean {
        const mappedActivities = this.asphaltEntityTypeActivityMap[asphaltEntry.entitytype_key];
        if (!mappedActivities?.length) {
            return false;
        }

        return mappedActivities.some(activity => executorActivities.has(activity));
    }

    private hasPlanning(project: Project, executor: User, date: string): boolean {
        const executorSchedule = this.executorScheduleArray.find(es => es.executor.id === executor.id);
        if (!executorSchedule) return false;

        const projectSchedule = executorSchedule.projects.find(
            ps => ps.project.afas_project_id === project.afas_project_id
        );
        if (!projectSchedule) return false;

        return !!projectSchedule.planningByDate[date];
    }

    private isNight(project: Project, executor: User, date: string): boolean {
        const executorSchedule = this.executorScheduleArray.find(es => es.executor.id === executor.id);
        if (!executorSchedule) return false;

        const projectSchedule = executorSchedule.projects.find(
            ps => ps.project.afas_project_id === project.afas_project_id
        );
        if (!projectSchedule) return false;

        const planningEntry = projectSchedule.planningByDate[date];
        if (!planningEntry) return false;

        return planningEntry.date.includes('T') && new Date(planningEntry.date).getHours() >= 20;
    }
}
